// src/utils/galleryImageProcessor.js

import { uploadBase64 } from '../services/storage-service';

/**
 * Process gallery images for Site Editor
 * Resizes to 1200px (full) and 400px (thumbnail), uploads both to Firebase Storage.
 * Falls back to Base64 if Storage is unavailable.
 * Max 12 images, 2MB each.
 */

/**
 * Render a File to two Base64 data URLs: full-size (1200px) and thumbnail (400px).
 * Returns a Promise so the async processGalleryImage can await it.
 */
function renderToBase64(file) {
  return new Promise((resolve, reject) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      reject(new Error(`Unsupported format. Use JPG, PNG, or WEBP.`));
      return;
    }

    // Validate file size (2MB max)
    const maxSize = 2 * 1024 * 1024;
    if (file.size > maxSize) {
      const sizeMB = (file.size / 1024 / 1024).toFixed(1);
      reject(new Error(`Too large: ${sizeMB}MB. Max 2MB.`));
      return;
    }

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          const maxWidth = 1200;
          const thumbWidth = 400;

          // Calculate dimensions for full-size image
          let fullWidth = img.width;
          let fullHeight = img.height;
          if (fullWidth > maxWidth) {
            fullHeight = (fullHeight * maxWidth) / fullWidth;
            fullWidth = maxWidth;
          }

          // Create full-size canvas
          const fullCanvas = document.createElement('canvas');
          fullCanvas.width = fullWidth;
          fullCanvas.height = fullHeight;
          const fullCtx = fullCanvas.getContext('2d');
          fullCtx.drawImage(img, 0, 0, fullWidth, fullHeight);
          const imageUrl = fullCanvas.toDataURL('image/jpeg', 0.85);

          // Create thumbnail canvas
          const thumbScale = thumbWidth / img.width;
          const thumbCanvas = document.createElement('canvas');
          thumbCanvas.width = thumbWidth;
          thumbCanvas.height = img.height * thumbScale;
          const thumbCtx = thumbCanvas.getContext('2d');
          thumbCtx.drawImage(img, 0, 0, thumbCanvas.width, thumbCanvas.height);
          const thumbnail = thumbCanvas.toDataURL('image/jpeg', 0.7);

          resolve({
            imageUrl,
            thumbnail,
            dimensions: { width: fullWidth, height: fullHeight },
            fileSize: file.size,
          });
        } catch (error) {
          reject(new Error('Failed to process image'));
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export async function processGalleryImage(file) {
  // Step 1: Resize and convert to Base64
  const { imageUrl: base64Full, thumbnail: base64Thumb, dimensions, fileSize } =
    await renderToBase64(file);

  // Step 2: Upload both to Firebase Storage
  const timestamp = Date.now();
  const safeName = file.name.replace(/[^a-zA-Z0-9.]/g, '_').substring(0, 30);
  const baseName = `${timestamp}_${safeName}`;

  try {
    const [fullResult, thumbResult] = await Promise.all([
      uploadBase64(`gallery/${baseName}`, base64Full),
      uploadBase64(`gallery/thumbs/${baseName}`, base64Thumb),
    ]);

    console.log(`✅ Gallery image uploaded to Storage`);

    return {
      imageUrl: fullResult.url,
      thumbnail: thumbResult.url,
      storagePath: fullResult.fullPath,
      thumbStoragePath: thumbResult.fullPath,
      dimensions,
      fileSize,
    };
  } catch (error) {
    // Fall back to Base64 if Storage is unavailable
    console.warn('⚠️ Gallery Storage upload failed, using Base64 fallback:', error.message);

    return {
      imageUrl: base64Full,
      thumbnail: base64Thumb,
      storagePath: null,
      thumbStoragePath: null,
      dimensions,
      fileSize,
    };
  }
}

/**
 * Generate alt text for gallery images
 * Priority: manual alt text > caption > auto-generated
 */
export function getAltText(image, practitionerName, index) {
  if (image.altText && image.altText.trim()) {
    return image.altText.trim();
  }
  if (image.caption && image.caption.trim()) {
    return image.caption.trim();
  }
  return `${practitionerName || 'Practice'} gallery image ${index + 1}`;
}
